import {
  GuestInfo,
  Guests,
  GuestSubmissionData,
  FormSubmissionData,
  FieldInfo,
  FormData,
  GroupOptions,
  RadiobuttonSubmissionData,
  CheckboxesSubmissionData,
  COMPONENT_TYPE_RADIOBUTTON,
  COMPONENT_TYPE_CHECKBOX,
  FIELD_ID_CUSTOM,
} from '@entities/leadCollector';

const getAllGuestFields = (guest: GuestInfo): FieldInfo[] => {
  return [...guest.FIO, ...(guest.additionalFields || [])];
};

const processFieldsToMainFields = (fields: FieldInfo[]): Record<string, string> => {
  if (!fields.length) {
    return {};
  }

  const mainFields: Record<string, string> = {};
  for (const field of fields) {
    if (!field.value) continue;

    const fieldName = (field.dbField === FIELD_ID_CUSTOM && field.label) || field.dbField;
    mainFields[fieldName] = field.value;
  }
  return mainFields;
};

const mapMainGuestToSubmissionData = (guest: GuestInfo): GuestSubmissionData => {
  const allFields = getAllGuestFields(guest);
  return processFieldsToMainFields(allFields);
};

const mapAdditionalGuestToSubmissionData = (guest: GuestInfo): GuestSubmissionData => {
  const allFields = getAllGuestFields(guest);
  return processFieldsToMainFields(allFields);
};

export const extractRadiobuttonsAndCheckboxes = (
  formData: FormData
): {
  radiobuttons?: RadiobuttonSubmissionData[];
  checkboxes?: CheckboxesSubmissionData[];
} => {
  const radiobuttons: RadiobuttonSubmissionData[] = [];
  const checkboxes: CheckboxesSubmissionData[] = [];

  for (const component of formData.components) {
    const isRadiobutton = component.type === COMPONENT_TYPE_RADIOBUTTON;
    const isCheckbox = component.type === COMPONENT_TYPE_CHECKBOX;

    if (!isRadiobutton && !isCheckbox) continue;

    const properties = component.properties as unknown as GroupOptions;

    if (!Array.isArray(properties?.options)) continue;

    const selectedValues = properties.options.reduce<string[]>((acc, option) => {
      if (!option.isActive || !option.text) return acc;
      acc.push(option.text);
      return acc;
    }, []);

    if (!selectedValues.length) continue;

    const currentTitle = properties.isTitleVisible && properties.title ? properties.title : undefined;

    if (!isRadiobutton) {
      checkboxes.push({
        titleCheckboxes: currentTitle,
        valueCheckboxes: selectedValues,
      });
      continue;
    }

    radiobuttons.push({
      titleRadiobutton: currentTitle,
      valueRadiobutton: selectedValues,
    });
  }

  const result: {
    radiobuttons?: RadiobuttonSubmissionData[];
    checkboxes?: CheckboxesSubmissionData[];
  } = {};

  if (radiobuttons.length) {
    result.radiobuttons = radiobuttons;
  }

  if (checkboxes.length) {
    result.checkboxes = checkboxes;
  }

  return result;
};

export const mapGuestsToSubmissionData = (guests: Guests): FormSubmissionData => {
  return {
    mainGuest: mapMainGuestToSubmissionData(guests.mainGuest),
    adultGuests: (guests.adultGuest || []).map((guest) => mapAdditionalGuestToSubmissionData(guest)),
    childrenGuests: (guests.childrenGuest || []).map((guest) => mapAdditionalGuestToSubmissionData(guest)),
  };
};
